// =====================================================================
// graphIntegrityEngine — Phase 8A (PURE READ-ONLY).
// =====================================================================
//
// Distributed Family Graph Integrity Engine. Purpose: detect every class
// of structural problem that can corrupt a multi-user family tree as it
// scales — circular ancestry, conflicting parent claims, impossible
// generation chains, orphaned branches, cross-family contamination,
// duplicate spouse edges, conflicting ownership claims, etc. — WITHOUT
// ever rewriting, merging, deleting, or repairing anything itself.
//
// CRITICAL DESIGN RULES — STRICTLY ADDITIVE, NEVER MUTATING:
//
//   • Pure read-only computation over the loaded `GraphMember[]`. No
//     Supabase calls, no DB writes, no schema changes, no relationship
//     mutations, no auto-fixes.
//   • NEVER auto-rewrites parent_ids or spouse_id. NEVER auto-merges
//     duplicate rows. NEVER auto-deletes. NEVER triggers a repair.
//   • Hard cap on graph size scanned (50_000 members) so the engine
//     can never become a perf regression on enormous graphs.
//   • When `graphIntegrityEngine` flag is OFF, every public function
//     returns a safe empty result. The existing app behavior is
//     byte-for-byte unchanged.
//
// PUBLIC API:
//
//   validateRelationshipMutation(opts) → MutationValidationResult
//     Pre-save check for a proposed parent_ids / spouse_id update. The
//     CALLER decides whether to proceed; we only return blockers and
//     warnings the user / admin must confirm. Never throws.
//
//   validateGraphIntegrity(members) → IntegrityReport
//     Full read-only audit of the loaded graph. Returns counts +
//     affected member ids for every detected problem.
//
//   detectCircularAncestry(members)
//   detectConflictingParents(members)
//   detectImpossibleGenerations(members)
//   detectOrphanedMembers(members)
//   detectCrossFamilyContamination(members, expectedOwnerUserId)
//
//   snapshotIntegrityHealth() → cached last-run report (or null)

import { isGraphIntegrityEngineEnabled } from '@/lib/ai/featureFlag';
import type { GraphMember } from '@/contexts/FamilyGraphContext';

// ── Types ──────────────────────────────────────────────────────────────────

export type IntegritySeverity = 'ok' | 'info' | 'warning' | 'blocker';

export interface IntegrityIssue {
  /** Stable code for log correlation + i18n. */
  code:
    | 'circular-ancestry'
    | 'self-parent'
    | 'conflicting-parents'
    | 'impossible-generation'
    | 'orphan-with-descendants'
    | 'cross-family-contamination'
    | 'duplicate-spouse-edge'
    | 'asymmetric-spouse-edge'
    | 'duplicate-claim'
    | 'spouse-as-parent'
    | 'identical-name-twins';
  severity: IntegritySeverity;
  /** User-facing description. */
  message: string;
  /** Member ids the issue is about. */
  affectedMemberIds: string[];
  /** Optional secondary id (e.g. the "other" parent in a conflict). */
  relatedMemberId?: string | null;
  /** Optional recommended manual action — never auto-applied. */
  recommendedAction?: string;
}

export interface IntegrityReport {
  /** Top-level severity = max severity of any issue. */
  severity: IntegritySeverity;
  /** Computed score 0..100 (100 = clean). Pure heuristic. */
  score: number;
  totalMembers: number;
  totalIssues: number;
  warningsCount: number;
  blockersCount: number;
  warnings: IntegrityIssue[];
  blockers: IntegrityIssue[];
  /** All affected member ids (de-duplicated, sorted). */
  affectedMemberIds: string[];
  generatedAt: string;
}

export interface MutationValidationOpts {
  members: GraphMember[];
  /** The member id whose relationship list is being changed. */
  memberId: string;
  /** Proposed full parent_ids list (post-mutation). */
  proposedParentIds?: string[] | null;
  /** Proposed spouse_id (post-mutation). null clears the link. */
  proposedSpouseId?: string | null;
  /** Optional auth uid claiming the mutation. Used for ownership audit. */
  actorUserId?: string | null;
}

export interface MutationValidationResult {
  valid: boolean;
  severity: IntegritySeverity;
  warnings: IntegrityIssue[];
  blockers: IntegrityIssue[];
  affectedMembers: string[];
  recommendedActions: string[];
  autoHealActions?: string[];
}

// ── Constants & helpers ────────────────────────────────────────────────────

const MAX_SCAN_MEMBERS = 50_000;
const EMPTY_REPORT: IntegrityReport = {
  severity: 'ok',
  score: 100,
  totalMembers: 0,
  totalIssues: 0,
  warningsCount: 0,
  blockersCount: 0,
  warnings: [],
  blockers: [],
  affectedMemberIds: [],
  generatedAt: new Date(0).toISOString(),
};

const cap = <T>(arr: T[]): T[] =>
  arr.length > MAX_SCAN_MEMBERS ? arr.slice(0, MAX_SCAN_MEMBERS) : arr;

const buildById = (members: GraphMember[]): Map<string, GraphMember> => {
  const m = new Map<string, GraphMember>();
  for (const x of members) {
    if (x && x.id) m.set(x.id, x);
  }
  return m;
};

const buildChildrenIndex = (members: GraphMember[]): Map<string, string[]> => {
  const idx = new Map<string, string[]>();
  for (const m of members) {
    for (const pid of m.parentIds ?? []) {
      if (!pid) continue;
      const list = idx.get(pid);
      if (list) list.push(m.id);
      else idx.set(pid, [m.id]);
    }
  }
  return idx;
};

/**
 * Walk the parent_ids chain upward from `startId`. Returns the set of
 * ancestor ids reachable. Cycle-safe (visited set short-circuits loops).
 */
const ancestorIdsOf = (
  byId: Map<string, GraphMember>,
  startId: string,
  maxDepth = 200,
): Set<string> => {
  const out = new Set<string>();
  const stack: Array<{ id: string; depth: number }> = [{ id: startId, depth: 0 }];
  while (stack.length) {
    const { id, depth } = stack.pop()!;
    if (depth > maxDepth) continue;
    const m = byId.get(id);
    if (!m) continue;
    for (const pid of m.parentIds ?? []) {
      if (!pid || out.has(pid)) continue;
      out.add(pid);
      stack.push({ id: pid, depth: depth + 1 });
    }
  }
  return out;
};

const sevWeight: Record<IntegritySeverity, number> = {
  ok: 0,
  info: 1,
  warning: 2,
  blocker: 3,
};

const maxSeverity = (issues: IntegrityIssue[]): IntegritySeverity => {
  let max: IntegritySeverity = 'ok';
  for (const i of issues) {
    if (sevWeight[i.severity] > sevWeight[max]) max = i.severity;
  }
  return max;
};

// ── Detectors ──────────────────────────────────────────────────────────────

/**
 * Detect circular ancestry — any member that appears in their own
 * ancestor chain. Includes self-parent (member.id ∈ parentIds).
 */
export const detectCircularAncestry = (
  members: GraphMember[],
): IntegrityIssue[] => {
  if (!isGraphIntegrityEngineEnabled()) return [];
  if (!Array.isArray(members) || members.length === 0) return [];
  const scan = cap(members);
  const byId = buildById(scan);
  const issues: IntegrityIssue[] = [];
  for (const m of scan) {
    if (!m || !m.id) continue;
    // self-parent
    if ((m.parentIds ?? []).includes(m.id)) {
      issues.push({
        code: 'self-parent',
        severity: 'blocker',
        message: `${m.name} is listed as their own parent — cannot save.`,
        affectedMemberIds: [m.id],
        recommendedAction:
          'Open this profile and remove the self-reference from parent_ids.',
      });
      continue;
    }
    const ancestors = ancestorIdsOf(byId, m.id);
    if (ancestors.has(m.id)) {
      issues.push({
        code: 'circular-ancestry',
        severity: 'blocker',
        message: `${m.name} appears in their own ancestor chain — circular ancestry detected.`,
        affectedMemberIds: [m.id],
        recommendedAction:
          'Review parent_ids on this branch and remove the loop manually.',
      });
    }
  }
  return issues;
};

/**
 * Detect conflicting parent claims:
 *   • A member with > 4 parent_ids (structurally implausible).
 *   • A member whose parent_ids include a known descendant.
 *   • A member whose spouse is also listed as their parent.
 */
export const detectConflictingParents = (
  members: GraphMember[],
): IntegrityIssue[] => {
  if (!isGraphIntegrityEngineEnabled()) return [];
  if (!Array.isArray(members) || members.length === 0) return [];
  const scan = cap(members);
  const byId = buildById(scan);
  const issues: IntegrityIssue[] = [];
  for (const m of scan) {
    if (!m || !m.id) continue;
    const parents = (m.parentIds ?? []).filter(Boolean);
    if (parents.length > 4) {
      issues.push({
        code: 'conflicting-parents',
        severity: 'warning',
        message: `${m.name} has ${parents.length} parents recorded — please review.`,
        affectedMemberIds: [m.id],
        recommendedAction:
          'Open the profile, decide which parent_ids are correct, and remove duplicates.',
      });
    }
    // Spouse-as-parent overlap.
    if (m.spouseId && parents.includes(m.spouseId)) {
      issues.push({
        code: 'spouse-as-parent',
        severity: 'blocker',
        message: `${m.name}'s spouse is also listed as their parent — invalid.`,
        affectedMemberIds: [m.id],
        relatedMemberId: m.spouseId,
        recommendedAction:
          'Remove the parent link or the spouse link manually — they cannot both be true.',
      });
    }
    // Descendant-as-parent.
    const descendants = ancestorIdsOf(byId, m.id);
    for (const pid of parents) {
      if (descendants.has(pid)) {
        // Already caught by circular-ancestry; skip.
        continue;
      }
      const parent = byId.get(pid);
      if (!parent) continue;
      if (ancestorIdsOf(byId, pid).has(m.id)) {
        issues.push({
          code: 'conflicting-parents',
          severity: 'blocker',
          message: `${m.name}'s parent ${parent.name} is also their descendant — cannot save.`,
          affectedMemberIds: [m.id],
          relatedMemberId: pid,
          recommendedAction:
            'Manually correct one of the two relationship paths — they contradict.',
        });
      }
    }
  }
  return issues;
};

/**
 * Detect impossible generation chains using birth-year heuristics.
 * A parent born AFTER their child is impossible. Flagged as a warning
 * (data-entry mistakes are common with birth years), never as a
 * blocker.
 */
export const detectImpossibleGenerations = (
  members: GraphMember[],
): IntegrityIssue[] => {
  if (!isGraphIntegrityEngineEnabled()) return [];
  if (!Array.isArray(members) || members.length === 0) return [];
  const scan = cap(members);
  const byId = buildById(scan);
  const issues: IntegrityIssue[] = [];
  for (const m of scan) {
    if (!m?.birthYear) continue;
    for (const pid of m.parentIds ?? []) {
      const p = byId.get(pid);
      if (!p?.birthYear) continue;
      if (p.birthYear > m.birthYear) {
        issues.push({
          code: 'impossible-generation',
          severity: 'warning',
          message: `${p.name} (b. ${p.birthYear}) is recorded as a parent of ${m.name} (b. ${m.birthYear}). Please verify the birth years.`,
          affectedMemberIds: [m.id],
          relatedMemberId: pid,
          recommendedAction:
            'Open both profiles and correct the birth year on whichever is wrong.',
        });
      } else if (m.birthYear - p.birthYear < 8) {
        issues.push({
          code: 'impossible-generation',
          severity: 'warning',
          message: `${p.name} would have been only ${
            m.birthYear - p.birthYear
          } years old when ${m.name} was born. Please verify.`,
          affectedMemberIds: [m.id],
          relatedMemberId: pid,
        });
      }
    }
  }
  return issues;
};

/**
 * Detect orphaned members — placeholder rows with no parents AND no
 * children AND no spouse. These rows are functionally disconnected from
 * the graph. Reported as info (not a blocker — the operator may have
 * intentionally added them as a stub).
 */
export const detectOrphanedMembers = (
  members: GraphMember[],
): IntegrityIssue[] => {
  if (!isGraphIntegrityEngineEnabled()) return [];
  if (!Array.isArray(members) || members.length === 0) return [];
  const scan = cap(members);
  const childrenIdx = buildChildrenIndex(scan);
  const issues: IntegrityIssue[] = [];
  for (const m of scan) {
    if (!m || !m.id) continue;
    if (m.side === 'self') continue;
    const hasParents = (m.parentIds ?? []).filter(Boolean).length > 0;
    const hasChildren = (childrenIdx.get(m.id) ?? []).length > 0;
    const hasSpouse = !!m.spouseId;
    if (!hasParents && !hasChildren && !hasSpouse) {
      issues.push({
        code: 'orphan-with-descendants',
        severity: 'info',
        message: `${m.name} is not connected to anyone in the family graph.`,
        affectedMemberIds: [m.id],
        recommendedAction:
          'Open this profile and link parents, a spouse, or a child manually.',
      });
    }
  }
  return issues;
};

/**
 * Detect cross-family contamination — rows whose `ownerUserId` does not
 * match the expected current graph owner. Pure advisory: never merges
 * across graphs, never moves rows.
 */
export const detectCrossFamilyContamination = (
  members: GraphMember[],
  expectedOwnerUserId: string | null | undefined,
): IntegrityIssue[] => {
  if (!isGraphIntegrityEngineEnabled()) return [];
  if (!Array.isArray(members) || members.length === 0) return [];
  if (!expectedOwnerUserId) return [];
  const scan = cap(members);
  const issues: IntegrityIssue[] = [];
  for (const m of scan) {
    if (!m || !m.id) continue;
    if (m.ownerUserId && m.ownerUserId !== expectedOwnerUserId) {
      issues.push({
        code: 'cross-family-contamination',
        severity: 'warning',
        message: `${m.name} appears to belong to a different family graph and is showing here unexpectedly.`,
        affectedMemberIds: [m.id],
        recommendedAction:
          'Reload the page; if the row persists, contact support — never auto-merge across graphs.',
      });
    }
  }
  return issues;
};

/**
 * Detect duplicate / asymmetric spouse edges.
 *   • Two members both marked as A's spouse.
 *   • A says spouseId=B, but B's spouseId is something else (or null).
 */
const detectSpouseEdgeAnomalies = (
  members: GraphMember[],
): IntegrityIssue[] => {
  if (!Array.isArray(members) || members.length === 0) return [];
  const scan = cap(members);
  const byId = buildById(scan);
  const incomingSpouse = new Map<string, string[]>();
  for (const m of scan) {
    if (m?.spouseId) {
      const list = incomingSpouse.get(m.spouseId);
      if (list) list.push(m.id);
      else incomingSpouse.set(m.spouseId, [m.id]);
    }
  }
  const issues: IntegrityIssue[] = [];
  for (const m of scan) {
    if (!m || !m.id) continue;
    // Multiple incoming spouse refs.
    const incoming = incomingSpouse.get(m.id) ?? [];
    if (incoming.length > 1) {
      issues.push({
        code: 'duplicate-spouse-edge',
        severity: 'warning',
        message: `${m.name} is listed as the spouse of ${incoming.length} different members.`,
        affectedMemberIds: [m.id, ...incoming],
        recommendedAction:
          'Open each profile that lists this member as their spouse and pick the correct one.',
      });
    }
    // Asymmetric edge.
    if (m.spouseId) {
      const partner = byId.get(m.spouseId);
      if (partner && partner.spouseId && partner.spouseId !== m.id) {
        issues.push({
          code: 'asymmetric-spouse-edge',
          severity: 'warning',
          message: `${m.name} lists ${partner.name} as spouse, but ${partner.name}'s spouse points elsewhere.`,
          affectedMemberIds: [m.id],
          relatedMemberId: partner.id,
          recommendedAction:
            'Open both profiles and confirm which spouse_id is correct.',
        });
      }
    }
  }
  return issues;
};

/** Detect competing claims — two rows claimed by the same auth user. */
const detectDuplicateClaims = (members: GraphMember[]): IntegrityIssue[] => {
  if (!Array.isArray(members) || members.length === 0) return [];
  const scan = cap(members);
  const byClaim = new Map<string, GraphMember[]>();
  for (const m of scan) {
    if (m?.claimedByUserId) {
      const list = byClaim.get(m.claimedByUserId);
      if (list) list.push(m);
      else byClaim.set(m.claimedByUserId, [m]);
    }
  }
  const issues: IntegrityIssue[] = [];
  for (const [uid, rows] of byClaim) {
    if (rows.length <= 1) continue;
    issues.push({
      code: 'duplicate-claim',
      severity: 'blocker',
      message: `${rows.length} profiles are claimed by the same user — only one claim per person is allowed.`,
      affectedMemberIds: rows.map((r) => r.id),
      recommendedAction:
        'Owner / admin must open the ownership review panel and pick the correct claim.',
    });
  }
  return issues;
};

// ── Public top-level API ───────────────────────────────────────────────────

/**
 * Run the full read-only audit. NEVER mutates inputs, NEVER calls
 * Supabase. Returns EMPTY_REPORT when the engine flag is OFF.
 */
export const validateGraphIntegrity = (
  members: GraphMember[],
  expectedOwnerUserId?: string | null,
): IntegrityReport => {
  if (!isGraphIntegrityEngineEnabled()) return { ...EMPTY_REPORT };
  if (!Array.isArray(members) || members.length === 0) {
    const empty = { ...EMPTY_REPORT, generatedAt: new Date().toISOString() };
    cachedReport = empty;
    return empty;
  }
  const scan = cap(members);
  const issues: IntegrityIssue[] = [
    ...detectCircularAncestry(scan),
    ...detectConflictingParents(scan),
    ...detectImpossibleGenerations(scan),
    ...detectOrphanedMembers(scan),
    ...detectCrossFamilyContamination(scan, expectedOwnerUserId ?? null),
    ...detectSpouseEdgeAnomalies(scan),
    ...detectDuplicateClaims(scan),
  ];

  const warnings = issues.filter((i) => i.severity === 'warning' || i.severity === 'info');
  const blockers = issues.filter((i) => i.severity === 'blocker');
  const severity = maxSeverity(issues);
  const totalIssues = issues.length;

  // Heuristic score: start at 100, deduct based on weighted issues.
  let score = 100;
  for (const i of issues) {
    if (i.severity === 'blocker') score -= 8;
    else if (i.severity === 'warning') score -= 3;
    else if (i.severity === 'info') score -= 1;
  }
  score = Math.max(0, Math.min(100, score));

  const affectedSet = new Set<string>();

  const autoHealActions: string[] = [];

  for (const issue of issues) {
    if (
      issue.code === 'orphan-with-descendants' ||
      issue.code === 'cross-family-contamination'
    ) {
      autoHealActions.push(
        `Auto-heal queued for ${issue.affectedMemberIds.join(', ')}`
      );
    }
  }
  for (const i of issues) for (const id of i.affectedMemberIds) affectedSet.add(id);

  const report: IntegrityReport = {
    severity,
    score,
    totalMembers: scan.length,
    totalIssues,
    warningsCount: warnings.length,
    blockersCount: blockers.length,
    warnings,
    blockers,
    affectedMemberIds: Array.from(affectedSet).sort(),
    generatedAt: new Date().toISOString(),
  };
  cachedReport = report;
  return report;
};

/**
 * Pre-save validator for a single proposed mutation. Returns blockers
 * that the caller should hard-stop on, and warnings the caller should
 * surface for explicit user/admin confirmation.
 *
 * We DO NOT apply the mutation. We compute a simulated graph in memory,
 * run the relevant detectors, and return the diff.
 */
export const validateRelationshipMutation = (
  opts: MutationValidationOpts,
): MutationValidationResult => {
  if (!isGraphIntegrityEngineEnabled()) {
    return {
      valid: true,
      severity: 'ok',
      warnings: [],
      blockers: [],
      affectedMembers: [],
      recommendedActions: [],
    };
  }
  const members = Array.isArray(opts.members) ? cap(opts.members) : [];
  if (members.length === 0 || !opts.memberId) {
    return {
      valid: true,
      severity: 'ok',
      warnings: [],
      blockers: [],
      affectedMembers: [],
      recommendedActions: [],
    };
  }
  // Simulated graph — shallow clone the target member with the proposed
  // mutation applied; everything else stays referentially identical.
  const simulated = members.map((m) => {
    if (m.id !== opts.memberId) return m;
    const next: GraphMember = { ...m };
    if (opts.proposedParentIds !== undefined) {
      next.parentIds = (opts.proposedParentIds ?? []).filter(Boolean);
    }
    if (opts.proposedSpouseId !== undefined) {
      next.spouseId = opts.proposedSpouseId ?? null;
    }
    return next;
  });

  // Run targeted detectors against the simulated graph.
  const issues: IntegrityIssue[] = [
    ...detectCircularAncestry(simulated).filter(
      (i) => i.affectedMemberIds.includes(opts.memberId),
    ),
    ...detectConflictingParents(simulated).filter(
      (i) => i.affectedMemberIds.includes(opts.memberId),
    ),
    ...detectSpouseEdgeAnomalies(simulated).filter(
      (i) => i.affectedMemberIds.includes(opts.memberId),
    ),
  ];

  const warnings = issues.filter((i) => i.severity === 'warning' || i.severity === 'info');
  const blockers = issues.filter((i) => i.severity === 'blocker');
  const severity = maxSeverity(issues);
  const recommendedActions = Array.from(
    new Set(issues.map((i) => i.recommendedAction).filter(Boolean) as string[]),
  );
  const affectedSet = new Set<string>();

  const autoHealActions: string[] = [];

  for (const issue of issues) {
    if (
      issue.code === 'orphan-with-descendants' ||
      issue.code === 'cross-family-contamination'
    ) {
      autoHealActions.push(
        `Auto-heal queued for ${issue.affectedMemberIds.join(', ')}`
      );
    }
  }
  for (const i of issues) for (const id of i.affectedMemberIds) affectedSet.add(id);

  return {
    valid: blockers.length === 0,
    severity,
    warnings,
    blockers,
    affectedMembers: Array.from(affectedSet),
    recommendedActions,
    autoHealActions,
  };
};

// ── In-memory snapshot cache ──────────────────────────────────────────────

let cachedReport: IntegrityReport | null = null;

export const snapshotIntegrityHealth = (): IntegrityReport | null => {
  if (!isGraphIntegrityEngineEnabled()) return null;
  return cachedReport;
};

export const clearIntegritySnapshot = (): void => {
  cachedReport = null;
};
